import { Box, List, ListItem, ListItemButton, Tooltip, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router';
import { useI18n } from '../../i18n/I18nProvider';
import { getSidebarSections } from './navigation';
import OrgSwitcher from './OrgSwitcher';
import { PersonAvatar } from '../../shared/ui/PersonAvatar';
import { BetaBadge } from '../../shared/ui/BetaBadge';
import { useOrganization } from '../../features/organization/OrganizationContext';
import { TriGlyph } from '../../shared/ui/Triangles';

/** Menu entries the help tours point at (src/help/tours). Stable names, never CSS classes. */
const TOUR_TARGETS: Record<string, string> = {
  '/kudos': 'nav-kudos',
  '/events': 'nav-events',
  '/learning': 'nav-learning',
};

interface SidebarMenuProps {
  user: any;
  onNavigate: () => void;
  onCreateOrg?: () => void;
  collapsed?: boolean;
}

export default function SidebarMenu({ user, onNavigate, collapsed = false }: SidebarMenuProps) {
  const location = useLocation();
  const { t } = useI18n();
  const { orgEnabledModules } = useOrganization();
  const { lmsItems, exploreItems, accountItems, managementLabel } = getSidebarSections(user, t, orgEnabledModules);

  // activeFor: other paths that belong to the entry (Contribute owns /use-cases/:id).
  const isActive = (item: { to: string; activeFor?: string[] }) =>
    [item.to, ...(item.activeFor || [])].some((path) => location.pathname.startsWith(path));

  if (collapsed) {
    const allItems = [...(user ? lmsItems : []), ...exploreItems, ...accountItems];
    return (
      <Box className="sidebar sidebar--collapsed ui-inverse">
        <Tooltip title={t('app.name')} placement="right" arrow>
          <Box component={Link} to="/dashboard" onClick={onNavigate} className="sidebar__rail-brand">
            <Box component="img" src="/favicon.svg" alt={t('app.name')} className="sidebar__rail-logo" />
            <BetaBadge className="beta-badge--rail" />
          </Box>
        </Tooltip>

        <Box className="sidebar__rail-nav">
          {allItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Tooltip key={item.to} title={item.label} placement="right" arrow>
                <Box
                  component={Link}
                  to={item.to}
                  onClick={onNavigate}
                  className={`sidebar__rail-item${active ? ' sidebar__rail-item--active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  data-tour={TOUR_TARGETS[item.to]}
                >
                  <Icon size={18} aria-hidden="true" />
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {user && (
          <Box className="sidebar__rail-user">
            <Tooltip title={user.name} placement="right" arrow>
              <span className="sidebar__rail-avatar-wrap">
                <PersonAvatar className="sidebar__rail-avatar" name={user.name || user.email} src={user.avatar} seed={user.id} size={30} />
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  }

  const navItem = (item: any) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      // ListItemButton renders an <a> here, and a <ul> may only contain <li>.
      // ListItem with disablePadding supplies the <li> and changes nothing
      // visually -- MUI’s own pattern for a list of links.
      <ListItem key={item.to} disablePadding>
        <ListItemButton
          component={Link}
          to={item.to}
          onClick={onNavigate}
          selected={active}
          aria-current={active ? 'page' : undefined}
          className="sidebar__nav-item"
          data-tour={TOUR_TARGETS[item.to]}
        >
          {/* The marker before the current page ("you are here"); aria-current says it to screen readers. */}
          {active && <span className="sidebar__marker" aria-hidden="true"><TriGlyph size="m" /></span>}
          <Box className="sidebar__nav-icon"><Icon size={18} aria-hidden="true" /></Box>
          <Typography component="span" className="sidebar__nav-label">{item.label}</Typography>
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box className="sidebar ui-inverse">
      <OrgSwitcher />

      <Box className="sidebar__scroll" data-tour="app-nav">
        {user && (
          <>
            <Typography className="sidebar__section-label">{t('navigation.learnAndGrow')}</Typography>
            <List dense disablePadding>{lmsItems.map(navItem)}</List>
          </>
        )}

        <Typography className={`sidebar__section-label${user ? ' sidebar__section-label--spaced' : ''}`}>
          {t('navigation.explore')}
        </Typography>
        <List dense disablePadding>{exploreItems.map(navItem)}</List>

        {accountItems.length > 0 && (
          <>
            <Typography className="sidebar__section-label sidebar__section-label--spaced">
              {managementLabel}
            </Typography>
            <List dense disablePadding>{accountItems.map(navItem)}</List>
          </>
        )}
      </Box>

      {/* The "Made with love" note moved to the app footer under every page, beside the
          Visma trademark line and the legal links (privacy plan item 8): one footer, not two. */}
    </Box>
  );
}
